// src/hooks/useCitasPaciente.js
import { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../api/api';

export const useCitasPaciente = (idPaciente) => {
  const [citasPendientes, setCitasPendientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 1. Envolvemos la función en useCallback para evitar recreaciones innecesarias
  // y cumplir con las reglas de ESLint.
  const fetchCitasData = useCallback(async () => {
    if (!idPaciente) return;
    
    setLoading(true);
    setError(null);
    
    const tabs = ['VAC', 'DES', 'PIP', 'CIR', 'TER', 'BAN'];
    
    // Promesas para tabs específicos
    const promesasTabs = tabs.map(tab =>
      api.get('/citas/tabs', {
        params: { id_paciente: idPaciente, id_tab: tab, es_pendiente: true }
      }).then(res => (Array.isArray(res.data) ? res.data.map(cita => ({ ...cita, tab })) : []))
      .catch(() => [])
    );

    // Promesa para consultas generales
    const promesaConsultas = api.get(`/citas/con-consultas-nombre/${idPaciente}`)
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
        return data.filter(cita => cita.es_express === false && cita.es_pendiente === true)
                   .map(cita => ({ ...cita, tab: 'CON' }));
      }).catch(() => []);

    try {
      const [resultadosTabs, resultadosConsultas] = await Promise.all([
        Promise.all(promesasTabs), 
        promesaConsultas
      ]);
      setCitasPendientes([...resultadosTabs.flat(), ...resultadosConsultas]);
    } catch (err) {
      setError("Error al cargar citas");
      setCitasPendientes([]);
    } finally {
      setLoading(false);
    }
  }, [idPaciente]); // La función solo cambia si el idPaciente cambia

  // 2. useEffect corregido sin warnings
  useEffect(() => {
    if (idPaciente) {
      fetchCitasData();
    } else {
      setCitasPendientes([]);
    }
  }, [idPaciente, fetchCitasData]); // fetchCitasData ahora es una dependencia estable

  // 3. Lógica de filtrado (se mantiene intacta)
  const categorizadas = useMemo(() => {
    if (!Array.isArray(citasPendientes)) return { vencidas: [], futuras: [] };
    
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    const v = [];
    const f = [];
    
    citasPendientes.forEach(cita => {
      if (!cita.fecha_cita) return;
      const fechaCita = new Date(cita.fecha_cita);
      if (fechaCita < hoy) { v.push(cita); } 
      else { f.push(cita); }
    });

    const sorter = (a, b) => (a.fecha_cita || '').localeCompare(b.fecha_cita || '');
    
    return { 
      vencidas: v.sort(sorter), 
      futuras: f.sort(sorter) 
    };
  }, [citasPendientes]);

  return {
    ...categorizadas,
    loading,
    error,
    refresh: fetchCitasData 
  };
};